import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  useTheme,
  Paper,
  Tooltip,
  Tabs,
  Tab,
  Badge,
  Chip,
  useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSwipeable } from 'react-swipeable';
import ConstructionIcon from '@mui/icons-material/Construction';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import EngineeringIcon from '@mui/icons-material/Engineering';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  parseISO,
  isSameDay,
  getDay,
  addDays,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths
} from 'date-fns';
import { ja } from 'date-fns/locale';
import { CombinedScheduleItem } from '../../types/commonSchedule';
import { useScheduleMasterData } from '../schedule/ScheduleDialog/useScheduleMasterData';
import { useResponsive } from '../../hooks/useResponsive';

interface ScheduleMonthViewProps {
  schedules: CombinedScheduleItem[];
  currentDate: Date;
  onDateClick: (date: Date, teamType: 'equipment' | 'scaffolding') => void;
  onScheduleClick: (schedule: CombinedScheduleItem) => void;
  onMonthChange: (newDate: Date) => void; // 月変更のコールバック（オプショナル）
}

const ScheduleMonthView: React.FC<ScheduleMonthViewProps> = ({
  schedules,
  currentDate,
  onDateClick,
  onScheduleClick,
  onMonthChange
}) => {
  const theme = useTheme();
  const { isMobile, isTablet, isDesktop } = useResponsive();

  // モバイル/タブレット用のタブ管理
  const [activeTab, setActiveTab] = useState<'equipment' | 'scaffolding'>('equipment');

  // スクロール管理
  const [isScrolling, setIsScrolling] = useState(false);
  const equipmentCalendarRef = useRef<HTMLDivElement>(null);
  const scaffoldingCalendarRef = useRef<HTMLDivElement>(null);

  // スワイプアニメーション用のstate
  const [swipeAnimation, setSwipeAnimation] = useState<{
    direction: 'prev' | 'next' | null;
    progress: number;
  }>({
    direction: null,
    progress: 0,
  });

  // スワイプハンドラーの設定
  const swipeHandlers = useSwipeable({
    onSwiping: (eventData) => {
      // 明確に水平方向のスワイプと判定できる場合のみ月の切り替えアニメーションを適用
      // 水平方向の動きが垂直方向の2.5倍以上ある場合のみ反応（2から2.5に増加）
      if (Math.abs(eventData.deltaX) > Math.abs(eventData.deltaY) * 2.5) {
        const direction = eventData.deltaX > 0 ? 'prev' : 'next';
        // アニメーションのスピードも調整（200から300に増加して感度を下げる）
        const progress = Math.min(Math.abs(eventData.deltaX) / 300, 1);
        setSwipeAnimation({ direction, progress });
      }
    },
    onSwiped: (eventData) => {
      // 1. しきい値を50から120に引き上げ
      // 2. 水平/垂直の比率を2から2.5に引き上げ
      // 3. スワイプ速度(velocity)の条件を追加（速いスワイプのみ反応）
      if (Math.abs(eventData.deltaX) > 120 &&
        Math.abs(eventData.deltaX) > Math.abs(eventData.deltaY) * 2.5 &&
        Math.abs(eventData.velocity) > 0.3) {

        // デバッグ用のコンソールログ（必要に応じて削除可能）
        console.log('月を変更します:', eventData.deltaX > 0 ? 'prev' : 'next');
        console.log('deltaX:', Math.abs(eventData.deltaX), 'velocity:', Math.abs(eventData.velocity));

        const direction = eventData.deltaX > 0 ? 'prev' : 'next';
        const newDate = direction === 'prev'
          ? subMonths(currentDate, 1)
          : addMonths(currentDate, 1);

        if (onMonthChange) {
          onMonthChange(newDate);
        }
      }

      setSwipeAnimation({ direction: null, progress: 0 });
    },
    trackTouch: true,
    trackMouse: false,  // モバイルのみに限定（PCでの誤操作を防止）
    delta: 15,          // スワイプ検出のしきい値を10から15に増加
    swipeDuration: 1000, // より短いスワイプ時間を許可（2000から1000に短縮）
    preventScrollOnSwipe: false,  // スクロールを許可
    touchEventOptions: { passive: true }  // パフォーマンス向上
  });



  // アニメーション用のスタイルを計算
  const getAnimationStyle = useCallback(() => {
    if (!swipeAnimation.direction || swipeAnimation.progress === 0) {
      return {};
    }

    return {
      transform: `translateX(${swipeAnimation.direction === 'prev' ? swipeAnimation.progress * 100 : -swipeAnimation.progress * 100}px)`,
      opacity: 1 - swipeAnimation.progress * 0.3,
      transition: swipeAnimation.progress === 0 ? 'transform 0.3s ease-out' : 'none',
    };
  }, [swipeAnimation]);

  // CSSクラスをheadに追加して-webkit-scrollbarを隠す
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .hide-scrollbar::-webkit-scrollbar {
        display: none;
      }
    `;

    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // カレンダー日付の計算（useMemoで最適化）
  const calendarDates = useMemo(() => {
    // 月の開始日と終了日
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);

    // カレンダー表示用の日付配列（前月と次月の日付を含む）
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // 月曜始まり
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = eachDayOfInterval({
      start: calendarStart,
      end: calendarEnd
    });

    // 週ごとに日付をグループ化
    const weeksArray: Date[][] = [];
    let week: Date[] = [];

    days.forEach((day, i) => {
      if (i % 7 === 0 && i > 0) {
        weeksArray.push([...week]);
        week = [];
      }
      week.push(day);
    });

    if (week.length > 0) {
      weeksArray.push([...week]);
    }

    return {
      days,
      weeks: weeksArray,
      start: calendarStart,
      end: calendarEnd
    };
  }, [currentDate]);



  // カレンダーマスターデータを取得するためのフック
  const { isWorkingDay, fetchWorkingDays } = useScheduleMasterData(true, 'construction');

  // タブ切り替え処理
  const handleTabChange = (_: React.SyntheticEvent, newValue: 'equipment' | 'scaffolding') => {
    setActiveTab(newValue);
  };

  // カレンダー日付の計算（useMemoで最適化）
  //   const calendarDates = useMemo(() => {
  //     // 月の開始日と終了日
  //     const monthStart = startOfMonth(currentDate);
  //     const monthEnd = endOfMonth(currentDate);

  //     // カレンダー表示用の日付配列（前月と次月の日付を含む）
  //     const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // 月曜始まり
  //     const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  //     const days = eachDayOfInterval({
  //       start: calendarStart,
  //       end: calendarEnd
  //     });

  //     // 週ごとに日付をグループ化
  //     const weeksArray: Date[][] = [];
  //     let week: Date[] = [];

  //     days.forEach((day, i) => {
  //       if (i % 7 === 0 && i > 0) {
  //         weeksArray.push([...week]);
  //         week = [];
  //       }
  //       week.push(day);
  //     });

  //     if (week.length > 0) {
  //       weeksArray.push([...week]);
  //     }

  //     return {
  //       days,
  //       weeks: weeksArray,
  //       start: calendarStart,
  //       end: calendarEnd
  //     };
  //   }, [currentDate]);

  // 設備班カレンダーの日付セルのクリックハンドラー
  const handleEquipmentDateClick = (date: Date) => {
    onDateClick(date, 'equipment');
  };

  // 仮設班カレンダーの日付セルのクリックハンドラー
  const handleScaffoldingDateClick = (date: Date) => {
    onDateClick(date, 'scaffolding');
  };

  // 指定日の予定を取得
  const getTeamSchedules = (date: Date, team: 'equipment' | 'scaffolding') => {
    return schedules.filter(s => {
      const scheduleDate = parseISO(s.scheduleDate);
      if (!isSameDay(scheduleDate, date)) return false;

      if (team === 'equipment') {
        return s.teamType === 'equipment' || s.teamType === undefined;
      } else {
        return s.teamType === 'scaffolding';
      }
    });
  };

  // 指定日の予定数を取得
  const getScheduleCount = (date: Date, team: 'equipment' | 'scaffolding') => {
    return getTeamSchedules(date, team).length;
  };

  // 日付のスタイルを会社休日対応に修正（レスポンシブ対応）
  const getDayStyle = (date: Date) => {
    const isToday = isSameDay(date, new Date());
    const isCompanyWorkDay = isWorkingDay(date);
    const isCurrentMonth = isSameMonth(date, currentDate);

    return {
      p: isMobile ? '0.2rem 0.2rem 1.7rem 0.2rem' : 0.5, // 下部にスペースを作成
      height: isMobile ? '70px' : isTablet ? '150px' : '240px',
      minHeight: isMobile ? '70px' : isTablet ? '150px' : '240px',
      cursor: 'pointer',
      border: '1px solid',
      borderColor: isToday ? theme.palette.primary.main : theme.palette.divider,
      overflow: 'hidden', // スクロールせずに切り捨て
      bgcolor: isCompanyWorkDay
        ? isToday
          ? alpha(theme.palette.primary.light, 0.15)
          : 'background.paper'
        : alpha(theme.palette.grey[200], 0.8),
      '&:hover': {
        bgcolor: alpha(theme.palette.action.hover, 0.7),
      },
      opacity: isCurrentMonth ? 1 : 0.5,
      position: 'relative'
    };
  };


  // カレンダー表示期間が変更されたときに稼働日情報を取得
  useEffect(() => {
    const refreshWorkingDays = async () => {
      // カレンダー表示用の日付範囲の文字列化
      const startDateStr = format(calendarDates.start, 'yyyy-MM-dd');
      const endDateStr = format(calendarDates.end, 'yyyy-MM-dd');
      await fetchWorkingDays(startDateStr, endDateStr);
    };

    refreshWorkingDays();
  }, [currentDate, calendarDates.start, calendarDates.end, fetchWorkingDays]);

  // モバイル表示用の簡易予定表示
  const renderMobileScheduleItems = (date: Date, team: 'equipment' | 'scaffolding') => {
    const teamSchedules = getTeamSchedules(date, team);

    if (teamSchedules.length === 0) {
      return null;
    }

    // 表示する予定の最大数（モバイルでは1件のみ表示）
    const firstSchedule = teamSchedules[0];
    const hasMore = teamSchedules.length > 1;

    // 会社名から株式会社などを省略する関数
    const formatCompanyName = (name: string): string => {
      const companyPatterns = ['株式会社', '有限会社', '合同会社', '(株)', '㈱'];
      for (const pattern of companyPatterns) {
        if (name.startsWith(pattern)) {
          return name.substring(pattern.length).trim();
        }
        if (name.endsWith(pattern)) {
          return name.substring(0, name.length - pattern.length).trim();
        }
      }
      return name;
    };

    return (
      <Box sx={{
        position: 'absolute',
        left: 1,
        right: 1,
        bottom: 1,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 0.3,
            borderLeft: '2px solid',
            borderLeftColor: firstSchedule.type === 'other'
              ? theme.palette.warning.main
              : team === 'equipment'
                ? theme.palette.info.main
                : theme.palette.warning.main,
            bgcolor: alpha(
              firstSchedule.type === 'other'
                ? theme.palette.warning.light
                : team === 'equipment'
                  ? theme.palette.info.light
                  : theme.palette.warning.light,
              0.1
            ),
            cursor: 'pointer',
            borderRadius: 0.5,
            '&:hover': {
              bgcolor: alpha(
                firstSchedule.type === 'other'
                  ? theme.palette.warning.light
                  : team === 'equipment'
                    ? theme.palette.info.light
                    : theme.palette.warning.light,
                0.2
              )
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            onScheduleClick(firstSchedule);
          }}
        >
          {/* 1行目: 現場名(トリミング) */}
          <Typography
            variant="caption"
            noWrap
            fontWeight="medium"
            sx={{ fontSize: '0.65rem' }}
          >
            {firstSchedule.type === 'other'
              ? (firstSchedule.scheduleContent || 'その他予定').substring(0, 8) + (firstSchedule.scheduleContent && firstSchedule.scheduleContent.length > 8 ? '...' : '')
              : (firstSchedule.siteName || `現場 ${firstSchedule.constructionId}`).substring(0, 8) + (firstSchedule.siteName && firstSchedule.siteName.length > 8 ? '...' : '')}
          </Typography>

          {/* 2行目: 客先(省略)・人数 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* 客先名(トリミング) */}
            {firstSchedule.customerName && (
              <Typography
                variant="caption"
                noWrap
                sx={{
                  fontSize: '0.6rem',
                  color: 'text.secondary',
                  maxWidth: '70%'
                }}
              >
                {formatCompanyName(firstSchedule.customerName).substring(0, 6) +
                  (formatCompanyName(firstSchedule.customerName).length > 6 ? '..' : '')}
              </Typography>
            )}

            {/* 必要人数（undefined対応） */}
            {firstSchedule.requiredWorkers !== undefined && firstSchedule.requiredWorkers > 0 && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 'bold',
                  color: (firstSchedule.memberData?.length || 0) >= (firstSchedule.requiredWorkers || 0)
                    ? theme.palette.success.main
                    : theme.palette.error.main
                }}
              >
                {firstSchedule.memberData?.length || 0}/{firstSchedule.requiredWorkers}
              </Typography>
            )}
          </Box>
        </Box>

        {/* 追加の予定がある場合は+Nで表示 */}
        {hasMore && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.55rem',
              textAlign: 'right',
              color: 'text.secondary',
              mt: 0.1
            }}
          >
            +{teamSchedules.length - 1}件
          </Typography>
        )}
      </Box>
    );
  };



  // タブレット表示用の簡易予定表示
  const renderTabletScheduleItems = (date: Date, team: 'equipment' | 'scaffolding') => {
    const teamSchedules = getTeamSchedules(date, team);

    if (teamSchedules.length === 0) {
      return null;
    }

    // 表示する予定の最大数
    const maxItems = 2;
    const hasMore = teamSchedules.length > maxItems;
    const displayedSchedules = teamSchedules.slice(0, maxItems);

    return (
      <Box sx={{ mt: 0.5 }}>
        {displayedSchedules.map((schedule, index) => (
          <Tooltip
            key={`${schedule.id}-${index}`}
            title={schedule.type === 'other'
              ? schedule.scheduleContent || 'その他予定'
              : schedule.siteName || `現場 ${schedule.constructionId}`
            }
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 0.3,
                mb: 0.2,
                borderLeft: '3px solid',
                borderLeftColor: schedule.type === 'other'
                  ? theme.palette.warning.main
                  : team === 'equipment'
                    ? theme.palette.info.main
                    : theme.palette.warning.main,
                bgcolor: alpha(
                  schedule.type === 'other'
                    ? theme.palette.warning.light
                    : team === 'equipment'
                      ? theme.palette.info.light
                      : theme.palette.warning.light,
                  0.1
                ),
                fontSize: '0.65rem',
                cursor: 'pointer',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                borderRadius: 0.5,
                '&:hover': {
                  bgcolor: alpha(
                    schedule.type === 'other'
                      ? theme.palette.warning.light
                      : team === 'equipment'
                        ? theme.palette.info.light
                        : theme.palette.warning.light,
                    0.2
                  )
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                onScheduleClick(schedule);
              }}
            >
              {schedule.type === 'other'
                ? <MeetingRoomIcon sx={{ fontSize: '0.75rem', mr: 0.3, color: theme.palette.warning.main }} />
                : <ConstructionIcon sx={{ fontSize: '0.75rem', mr: 0.3, color: theme.palette.info.main }} />
              }
              <Typography
                variant="caption"
                noWrap
                sx={{ fontSize: '0.65rem' }}
              >
                {schedule.type === 'other'
                  ? schedule.scheduleContent || 'その他予定'
                  : schedule.siteName || `現場 ${schedule.constructionId}`}
              </Typography>
            </Box>
          </Tooltip>
        ))}

        {hasMore && (
          <Chip
            label={`+${teamSchedules.length - maxItems}`}
            size="small"
            variant="outlined"
            sx={{
              height: 16,
              fontSize: '0.6rem',
              '& .MuiChip-label': { px: 0.5 }
            }}
          />
        )}
      </Box>
    );
  };

  // デスクトップ表示用の詳細予定表示
  const renderDesktopScheduleItems = (date: Date, team: 'equipment' | 'scaffolding') => {
    const teamSchedules = getTeamSchedules(date, team);

    if (teamSchedules.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mt: 1 }}>
        {teamSchedules.map((schedule, index) => (
          <Paper
            key={`${schedule.id}-${index}`}
            elevation={0}
            sx={{
              mb: 0.5,
              p: 0.5,
              borderLeft: '3px solid',
              borderLeftColor: schedule.type === 'other'
                ? theme.palette.warning.main
                : team === 'scaffolding'
                  ? theme.palette.warning.main
                  : theme.palette.info.main,
              background: schedule.type === 'other'
                ? 'linear-gradient(to right, white, #fff8e1)'
                : 'linear-gradient(to right, white, #f9f9f9)',
              fontSize: '0.75rem',
              maxHeight: 65,
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                transform: 'translateY(-1px)'
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              onScheduleClick(schedule);
            }}
          >
            <Box display="flex" alignItems="center" mb={0.5}>
              {schedule.type === 'other' ? (
                <MeetingRoomIcon fontSize="small" sx={{ mr: 0.5, fontSize: '0.9rem', color: theme.palette.warning.dark }} />
              ) : (
                <ConstructionIcon fontSize="small" sx={{ mr: 0.5, fontSize: '0.9rem', color: theme.palette.info.dark }} />
              )}
              <Tooltip title={
                schedule.type === 'other'
                  ? schedule.scheduleContent || 'その他予定'
                  : schedule.siteName || `現場 ${schedule.constructionId}`
              }>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  noWrap
                  sx={{ flex: 1 }}
                >
                  {schedule.type === 'other'
                    ? schedule.scheduleContent || 'その他予定'
                    : schedule.siteName || `現場 ${schedule.constructionId}`}
                </Typography>
              </Tooltip>
            </Box>

            {/* 客先名の表示 */}
            {schedule.customerName && (
              <Tooltip title={schedule.customerName}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: 'block', mb: 0.3 }}
                >
                  {schedule.customerName}
                </Typography>
              </Tooltip>
            )}

            {/* 必要人数と現在の人数の表示 */}
            {schedule.requiredWorkers && schedule.requiredWorkers > 0 ? (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                mt: 0.2
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: (schedule.memberData?.length || 0) >= schedule.requiredWorkers
                      ? theme.palette.success.dark
                      : theme.palette.error.dark,
                    fontWeight: 'bold',
                    fontSize: '0.65rem'
                  }}
                >
                  {schedule.memberData?.length || 0}/{schedule.requiredWorkers}人
                </Typography>
              </Box>
            ) : null}
          </Paper>
        ))}
      </Box>
    );
  };

  // 画面サイズに応じて最適な予定表示を選択
  const renderScheduleItems = (date: Date, team: 'equipment' | 'scaffolding') => {
    if (isMobile) {
      return renderMobileScheduleItems(date, team);
    } else if (isTablet) {
      return renderTabletScheduleItems(date, team);
    } else {
      return renderDesktopScheduleItems(date, team);
    }
  };

  // 日付セル内の日付表示
  const renderDateCell = (date: Date) => {
    const isToday = isSameDay(date, new Date());
    const isCompanyWorkDay = isWorkingDay(date);

    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 0.2
      }}>
        <Typography
          variant="body2"
          fontWeight={isToday ? "bold" : "normal"}
          color={!isCompanyWorkDay ? "error.main" : isToday ? "primary.main" : "text.primary"}
          sx={isToday ? {
            borderRadius: '8px',
            display: 'inline-block',
            px: isMobile ? 0.3 : 1,
            py: isMobile ? 0 : 0.2,
            bgcolor: 'primary.main',
            color: 'white',
            fontSize: isMobile ? '0.65rem' : '0.875rem'
          } : {
            fontSize: isMobile ? '0.65rem' : '0.875rem'
          }}
        >
          {format(date, 'd')}
        </Typography>

        {/* 休日の場合は小さく「休」を表示 */}
        {!isCompanyWorkDay && (
          <Typography
            variant="caption"
            color="error"
            sx={{
              fontSize: '0.55rem',
              fontWeight: 'bold'
            }}
          >
            休
          </Typography>
        )}
      </Box>
    );
  };


  // カレンダーグリッドの曜日ヘッダー
  const renderDayHeader = () => {
    return (
      <Grid container>
        {['月', '火', '水', '木', '金', '土', '日'].map((day, index) => (
          <Grid item xs={12 / 7} key={`day-header-${day}`}>
            <Box
              textAlign="center"
              p={isMobile ? 0.3 : 0.5}
              bgcolor={theme.palette.grey[200]}
              color={index >= 5 ? (index === 5 ? 'primary.dark' : 'error.main') : 'text.primary'}
            >
              <Typography
                variant={isMobile ? "caption" : "body2"}
                fontWeight="bold"
              >
                {day}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  // カレンダーグリッドの週の行
  const renderWeek = (week: Date[], teamType: 'equipment' | 'scaffolding', weekIndex: number) => {
    return (
      <Grid container key={`${teamType}-week-${weekIndex}`}>
        {week.map((day, dayIndex) => {
          const dateClickHandler = teamType === 'equipment'
            ? handleEquipmentDateClick
            : handleScaffoldingDateClick;

          return (
            <Grid item xs={12 / 7} key={`${teamType}-day-${weekIndex}-${dayIndex}`}>
              <Box
                sx={getDayStyle(day)}
                onClick={() => dateClickHandler(day)}
              >
                {renderDateCell(day)}
                {renderScheduleItems(day, teamType)}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // チームヘッダーを表示
  const renderTeamHeader = (teamType: 'equipment' | 'scaffolding') => {
    return (
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 0.5 : 1,
          background: teamType === 'equipment'
            ? 'linear-gradient(135deg, #2196f3, #1976d2)'
            : 'linear-gradient(135deg, #ffa726, #f57c00)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '0',
        }}
      >
        {teamType === 'equipment' ? (
          <SettingsInputComponentIcon sx={{ color: 'white', mr: 1 }} />
        ) : (
          <EngineeringIcon sx={{ color: 'white', mr: 1 }} />
        )}
        <Typography
          variant={isMobile ? "subtitle1" : "h6"}
          sx={{ color: 'white', fontWeight: 'medium' }}
        >
          {teamType === 'equipment' ? '設備班' : '仮設班'}
        </Typography>
      </Paper>
    );
  };

  // カレンダーコンテンツを表示
  const renderCalendarContent = (teamType: 'equipment' | 'scaffolding') => {
    const ref = teamType === 'equipment' ? equipmentCalendarRef : scaffoldingCalendarRef;

    return (
      <div
        ref={ref}
        className="hide-scrollbar"
        style={{
          overflowY: 'auto',
          height: 'calc(100vh - 250px)',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: isScrolling ? 'auto' : 'smooth',
          touchAction: 'pan-y',  // 'none' から 'pan-y' に変更
          WebkitUserSelect: 'none',
          userSelect: 'none',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <Box>
          {renderDayHeader()}
          {calendarDates.weeks.map((week, index) =>
            renderWeek(week, teamType, index)
          )}
        </Box>
      </div>
    );
  };




  // チームカレンダーを表示
  const renderTeamCalendar = (teamType: 'equipment' | 'scaffolding') => {
    return (
      <Box sx={{
        flex: 1,
        bgcolor: 'white',
        borderRadius: { xs: '4px 4px 0 0', sm: '8px 8px 0 0' },
        overflow: 'hidden',
        boxShadow: '0 0 8px rgba(0,0,0,0.08)',
        border: '2px solid',
        borderColor: teamType === 'equipment' ? '#1976d2' : '#f57c00',
      }}>
        {renderTeamHeader(teamType)}
        {renderCalendarContent(teamType)}
      </Box>
    );
  };

  // モバイル・タブレット表示の場合はタブで切り替え
  if (isMobile || isTablet) {
    return (
      <Box
        {...swipeHandlers}
        sx={{
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          touchAction: 'pan-y'  // この行を追加
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            mb: 1,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontWeight: 'medium',
              fontSize: { xs: '0.8rem', sm: '0.9rem' }
            }
          }}
        >
          <Tab
            value="equipment"
            label="設備班"
            icon={<SettingsInputComponentIcon color="info" />}
            iconPosition="start"
          />
          <Tab
            value="scaffolding"
            label="仮設班"
            icon={<EngineeringIcon color="warning" />}
            iconPosition="start"
          />
        </Tabs>

        {/* スワイプアニメーション付きのコンテンツ */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            ...getAnimationStyle()
          }}
        >
          <Box sx={{ display: activeTab === 'equipment' ? 'block' : 'none', width: '100%' }}>
            {renderTeamCalendar('equipment')}
          </Box>

          <Box sx={{ display: activeTab === 'scaffolding' ? 'block' : 'none', width: '100%' }}>
            {renderTeamCalendar('scaffolding')}
          </Box>
        </Box>

        {/* スワイプ方向のヒント */}
        <Box sx={{
          textAlign: 'center',
          color: 'text.secondary',
          margin: '8px 0',
          padding: '4px',
          border: '1px dashed #ccc',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          ↑↓ 縦スワイプでスクロール、← → 横スワイプで月切替
        </Box>
      </Box>
    );
  }

  // デスクトップ表示の場合は横並び・スワイプ可能
  return (
    <Box
      {...swipeHandlers}
      sx={{
        width: '100%',
        display: 'flex',
        gap: 2,
        position: 'relative',
        overflow: 'hidden',
        touchAction: 'pan-y'  // 垂直スクロールを許可、水平スワイプのみ処理 
      }}
    >
      {/* スワイプアニメーション付きのコンテンツ */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          gap: 2,
          ...getAnimationStyle()
        }}
      >
        {renderTeamCalendar('equipment')}
        {renderTeamCalendar('scaffolding')}
      </Box>

      {/* スワイプ方向のヒント */}
      <Box sx={{
        position: 'fixed',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        textAlign: 'center',
        color: 'text.secondary',
        padding: '8px 16px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        zIndex: 10
      }}>
        ↑↓ 縦スワイプでスクロール、← → 横スワイプで月切替
      </Box>
    </Box>
  );
};

export default ScheduleMonthView;